import type { AgentReleaseView, JobView } from '@api/devices/contracts';
import { ShieldCheck, X } from 'lucide-react';
import { Dialog } from 'radix-ui';
import { useEffect, useState } from 'react';
import { formatBytes } from '../lib/format';
import { absolute } from '../lib/time';
import { type DeviceRef, useCapabilities, useJobActions } from '../operations/jobs';
import { Button } from '../ui/Button';
import { CopyButton } from '../ui/misc';

const releaseVersion = (v: string) => {
  if (!/^\d+\.\d+\.\d+$/.test(v)) return null;
  const parts = v.split('.').map(Number);
  return parts.every(Number.isSafeInteger) ? parts : null;
};

/** The newest release newer than what the device runs, if any (plain `a.b.c` versions). */
export function newerRelease(releases: AgentReleaseView[] | undefined, running: string | undefined): AgentReleaseView | null {
  const current = running ? releaseVersion(running) : null;
  if (!current) return null;
  const newer = (releases ?? []).filter(r => {
    const v = releaseVersion(r.version);
    if (!v) return false;
    const i = v.findIndex((n, k) => n !== current[k]);
    return i >= 0 && v[i]! > current[i]!;
  });
  return newer[0] ?? null; // The server lists newest first.
}

/** Release status for the device page, separate from permission to request an update. */
export function agentReleaseState(releases: AgentReleaseView[] | undefined, running: string | undefined,
  os: string | null, architecture: string | undefined, updating: boolean): 'current' | 'updating' | undefined {
  if (!running) return undefined;
  if (updating) return 'updating';
  if (!releaseVersion(running)) return undefined;
  // Match the API's platform selection, including legacy Windows inventory without an architecture.
  const platform = os === 'windows' && (!architecture || architecture === 'x64') ? 'windows-x64'
    : os === 'windows' && architecture === 'arm64' ? 'windows-arm64'
    : os === 'linux' && (architecture === 'x64' || architecture === 'arm64') ? `linux-${architecture}`
    : os === 'macos' && architecture === 'arm64' ? 'darwin-arm64' : null;
  const known = releases?.filter(r => r.platform === platform && releaseVersion(r.version));
  return known?.length && !newerRelease(known, running) ? 'current' : undefined;
}

/** Request an agent update for one device. It changes the device, so it always goes to approval. */
export function UpdateAgentDialog({ release, running, device, open, onOpenChange, onNeedsApproval }: {
  release: AgentReleaseView | null; running: string; device: DeviceRef; open: boolean; onOpenChange: (open: boolean) => void;
  onNeedsApproval: (job: JobView) => void;
}) {
  const { create } = useJobActions();
  const { approve: canApprove } = useCapabilities(device);
  const [reason, setReason] = useState('');
  useEffect(() => { if (open && release) setReason(`Update the agent to ${release.version}`); }, [open, release]);
  if (!release) return null;
  const submit = () => create.mutate({ deviceId: device.id, reason: reason.trim() || `Update the agent to ${release.version}`, update: { version: release.version } }, {
    onSuccess: created => { onOpenChange(false); if (canApprove) onNeedsApproval(created); },
  });
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 animate-fade" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[90dvh] overflow-y-auto rounded-t-2xl border border-line bg-raised p-5 shadow-pop animate-pop sm:inset-auto sm:left-1/2 sm:top-[10vh] sm:w-[min(560px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:rounded-2xl">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold">Update the agent to {release.version}</Dialog.Title>
              <Dialog.Description className="mt-0.5 text-[13px] text-ink-2">
                {device.title} runs {running}. The device checks that this release is signed by WITTCO and that the download matches it, installs it,
                and goes back to {running} by itself if the new version doesn’t start and stay up.
              </Dialog.Description>
            </div>
            <Dialog.Close className="rounded-md p-1 text-ink-3 hover:bg-hover" aria-label="Close"><X className="size-4" /></Dialog.Close>
          </div>
          <dl className="mt-4 divide-y divide-line/70 rounded-xl border border-line px-3 text-[13px]">
            <div className="flex gap-3 py-2"><dt className="w-28 shrink-0 text-ink-3">Package</dt>
              <dd className="flex min-w-0 items-center gap-1 font-mono text-[12px]">{formatBytes(release.size)} · SHA-256 {release.sha256.slice(0, 16)}…<CopyButton value={release.sha256} label="Copy SHA-256" /></dd></div>
            <div className="flex gap-3 py-2"><dt className="w-28 shrink-0 text-ink-3">Published</dt><dd>{absolute(release.publishedAt)}</dd></div>
            <div className="flex gap-3 py-2"><dt className="w-28 shrink-0 text-ink-3">Security floor</dt>
              <dd>{release.floor === release.version ? 'After this update, no older release can be installed on this device' : `After this update, releases older than ${release.floor} can’t be installed on this device`}</dd></div>
          </dl>
          <label className="mt-4 block text-[13.5px]"><span className="text-ink-2">Reason</span>
            <input value={reason} onChange={e => setReason(e.target.value)} maxLength={500}
              className="mt-1 h-9 w-full rounded-lg border border-line bg-panel px-3 outline-none focus:border-brand/50" /></label>
          <p className="mt-3 text-[12px] text-ink-3">The agent restarts during the update, so a job running on the device at that moment is stopped.</p>
          <div className="mt-4 flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost">Cancel</Button></Dialog.Close>
            <Button variant="primary" disabled={create.isPending} onClick={submit}><ShieldCheck className="size-4" />Request approval</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
